import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useRazorpay } from "react-razorpay";
import {
    Loader2, Plane, ShieldCheck, Copy, CheckCircle2, Lock, User, ArrowLeft, Sparkles,
} from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Sections5";
import {
    fetchFlightSearch, fetchFlightMeta, createFlightBooking, createFlightPayment,
    verifyFlightPayment, getCustomerToken, formatApiError,
} from "@/lib/api";
import { inr, fmtTime, fmtDate, fmtDur } from "@/pages/FlightResultsPage";

const newPassenger = (type) => ({ type, first_name: "", last_name: "", dob: "", gender: "", passport_no: "", passport_expiry: "" });

export default function FlightCheckoutPage() {
    const { searchId } = useParams();
    const navigate = useNavigate();
    const { Razorpay } = useRazorpay();
    const [data, setData] = useState(null);
    const [meta, setMeta] = useState(null);
    const [selection, setSelection] = useState(null);
    const [passengers, setPassengers] = useState([]);
    const [contact, setContact] = useState({ name: "", email: "", phone: "" });
    const [refCode, setRefCode] = useState("");
    const [consent, setConsent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [booking, setBooking] = useState(null);
    const [payBusy, setPayBusy] = useState(false);
    const idemKey = useRef(crypto.randomUUID());

    useEffect(() => {
        let sel = null;
        try { sel = JSON.parse(sessionStorage.getItem("gc_flight_selection") || "null"); } catch { sel = null; }
        if (!sel || sel.search_id !== searchId) {
            toast.error("Pick your flights first");
            navigate(`/flights/results/${searchId}`);
            return;
        }
        setSelection(sel);
        Promise.all([fetchFlightSearch(searchId), fetchFlightMeta()])
            .then(([d, m]) => {
                setData(d); setMeta(m);
                const list = [];
                for (let i = 0; i < d.pax.adults; i++) list.push(newPassenger("adult"));
                for (let i = 0; i < (d.pax.children || 0); i++) list.push(newPassenger("child"));
                for (let i = 0; i < (d.pax.infants || 0); i++) list.push(newPassenger("infant"));
                setPassengers(list);
            })
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    }, [searchId, navigate]);

    const offers = useMemo(() => {
        if (!data || !selection) return [];
        return selection.offer_ids.map((oid, i) =>
            (data.offers_by_leg[String(i)] || []).find((o) => o.id === oid)).filter(Boolean);
    }, [data, selection]);

    const international = useMemo(() => {
        if (!data) return false;
        return Object.values(data.airports || {}).some((a) => a.country !== "India");
    }, [data]);

    const estimate = useMemo(() => {
        if (!offers.length || !data) return null;
        const { adults, children = 0, infants = 0 } = data.pax;
        const cFactor = Math.round(children * 0.75);
        const base = offers.reduce((s, o) => s + o.fare.base * (adults + cFactor) + Math.round(o.fare.base * 0.10) * infants, 0);
        const taxes = offers.reduce((s, o) => s + o.fare.taxes * (adults + children), 0);
        return { base, taxes, total: base + taxes };
    }, [offers, data]);

    const setPax = (i, patch) => setPassengers((ps) => ps.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

    const submit = async () => {
        if (!consent) { toast.error("Please accept the fare rules and terms"); return; }
        for (const p of passengers) {
            if (!p.first_name.trim() || !p.last_name.trim()) { toast.error("Every passenger needs a first and last name (as on govt ID)"); return; }
            if ((p.type === "child" || p.type === "infant") && !p.dob) { toast.error("Date of birth is required for children and infants"); return; }
        }
        if (!contact.name.trim() || !/.+@.+\..+/.test(contact.email)) { toast.error("Contact name and a valid email are required"); return; }
        if (!/^\d{10}$/.test(contact.phone.replace(/\D/g, "").slice(-10))) { toast.error("Enter a valid 10-digit contact mobile"); return; }
        setBusy(true);
        try {
            const res = await createFlightBooking({
                search_id: searchId,
                offer_ids: selection.offer_ids,
                passengers: passengers.map((p) => ({
                    ...p,
                    dob: p.dob || null, gender: p.gender || null,
                    passport_no: p.passport_no || null, passport_expiry: p.passport_expiry || null,
                })),
                contact,
                associate_code: refCode.trim() || null,
                consent: true,
                idempotency_key: idemKey.current,
            }, getCustomerToken() || undefined);
            setBooking(res.booking);
            window.scrollTo({ top: 0, behavior: "smooth" });
            toast.success(res.duplicate ? "This booking was already created — here's your reference" : "Booking request created!");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally { setBusy(false); }
    };

    const payNow = async () => {
        setPayBusy(true);
        try {
            const order = await createFlightPayment(booking.reference);
            const rzp = new Razorpay({
                key: order.key_id, amount: order.amount * 100, currency: order.currency,
                name: "GhumneChaley.com", description: `Flight booking ${booking.reference}`,
                order_id: order.order_id,
                prefill: { name: contact.name, email: contact.email, contact: contact.phone },
                theme: { color: "#F26522" },
                handler: async (resp) => {
                    try {
                        const r = await verifyFlightPayment({
                            order_id: resp.razorpay_order_id, payment_id: resp.razorpay_payment_id,
                            signature: resp.razorpay_signature,
                        });
                        setBooking(r.booking);
                        toast.success("Payment received — your ticket is being issued");
                    } catch (err) {
                        toast.error(formatApiError(err.response?.data?.detail));
                    }
                },
                modal: { ondismiss: () => toast("Payment window closed — nothing was charged.") },
            });
            rzp.on("payment.failed", (resp) => toast.error(`Payment failed: ${resp.error?.description || "try another method"}`));
            rzp.open();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally { setPayBusy(false); }
    };

    if (!data || !selection) {
        return <div className="min-h-screen bg-cream flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-saffron" data-testid="checkout-loading" /></div>;
    }

    // ---------------- success view ----------------
    if (booking) {
        const paid = booking.payment?.status === "paid";
        return (
            <div className="min-h-screen bg-cream" data-testid="booking-success-page">
                <Header />
                <main className="max-w-2xl mx-auto px-4 py-12">
                    <div className="rounded-2xl border border-stone-200 bg-white p-6 sm:p-8 shadow-xl">
                        <CheckCircle2 className="h-12 w-12 text-green-600" />
                        <h1 className="mt-3 font-display font-black text-2xl sm:text-3xl text-navy">
                            {booking.demo ? "Demo booking created" : "Booking request received"}
                        </h1>
                        <p className="mt-1 text-sm text-stone-500">
                            {booking.demo
                                ? "This is a demonstration booking on sample fares — no payment is taken and no real ticket exists yet."
                                : "Our team is processing your booking. You'll get an email at every step."}
                        </p>
                        <div className="mt-5 flex items-center gap-3 rounded-xl bg-cream border border-stone-200 px-4 py-3">
                            <div className="flex-1">
                                <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Booking reference</p>
                                <p className="font-display font-black text-xl text-navy" data-testid="booking-reference">{booking.reference}</p>
                            </div>
                            <button data-testid="copy-reference" onClick={() => { navigator.clipboard.writeText(booking.reference); toast.success("Copied"); }}
                                className="rounded-full border border-stone-300 p-2.5 text-navy hover:border-saffron"><Copy className="h-4 w-4" /></button>
                        </div>
                        <div className="mt-4 space-y-2 text-sm">
                            {booking.offers.map((o, i) => (
                                <div key={i} className="flex items-center gap-3 rounded-xl border border-stone-100 bg-cream/50 px-4 py-2.5">
                                    <Plane className="h-4 w-4 text-saffron rotate-90 shrink-0" />
                                    <span className="font-semibold text-navy">{o.segments[0].from} → {o.segments[o.segments.length - 1].to}</span>
                                    <span className="text-stone-500 text-xs">{o.airline} {o.flight_nos[0]} · {fmtDate(o.segments[0].depart)}</span>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 rounded-xl border border-stone-200 p-4 text-sm">
                            <p className="font-bold text-navy mb-2">Fare summary</p>
                            {[["Base fare", booking.pricing.base_fare], ["Taxes & airline fees", booking.pricing.taxes + booking.pricing.supplier_fee],
                                ...(booking.pricing.service_fee ? [["Service fee", booking.pricing.service_fee]] : []),
                                ...(booking.pricing.markup ? [["Service charge", booking.pricing.markup]] : [])].map(([l, v]) => (
                                <div key={l} className="flex justify-between py-1 text-stone-600"><span>{l}</span><span>{inr(v)}</span></div>
                            ))}
                            <div className="flex justify-between pt-2 mt-1 border-t border-stone-200 font-display font-black text-navy text-base">
                                <span>Total payable</span><span data-testid="booking-total">{inr(booking.pricing.total)}</span>
                            </div>
                        </div>
                        <div className="mt-5">
                            {meta?.booking_enabled && !paid ? (
                                <button data-testid="pay-now-btn" onClick={payNow} disabled={payBusy}
                                    className="w-full rounded-full bg-saffron text-white font-display font-black text-lg py-3.5 hover:bg-navy transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                                    {payBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Lock className="h-4 w-4" /> Pay {inr(booking.pricing.total)} securely</>}
                                </button>
                            ) : paid ? (
                                <p className="rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-sm font-bold text-green-700" data-testid="paid-note">
                                    Payment received. Your ticket is being issued — “Ticket Confirmed” appears only after the airline confirms issuance.
                                </p>
                            ) : (
                                <p className="rounded-xl bg-gold/15 border border-gold/60 px-4 py-3 text-sm text-navy" data-testid="payment-disabled-note">
                                    <b>Online payment is off during the supplier-preview phase.</b> Save your reference —
                                    you can track this booking anytime under <Link to="/my-trips" className="text-saffron font-bold underline">My Trips</Link>.
                                </p>
                            )}
                        </div>
                        <div className="mt-4 flex gap-3">
                            <Link to="/my-trips" data-testid="go-my-trips" className="flex-1 text-center rounded-full border-2 border-navy py-2.5 font-bold text-navy hover:bg-navy hover:text-cream transition-colors">Track in My Trips</Link>
                            <Link to="/flights" data-testid="book-another" className="flex-1 text-center rounded-full border-2 border-stone-200 py-2.5 font-bold text-stone-500 hover:border-saffron hover:text-saffron transition-colors">New search</Link>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    // ---------------- form view ----------------
    return (
        <div className="min-h-screen bg-cream" data-testid="flight-checkout-page">
            <Header />
            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
                <Link to={`/flights/results/${searchId}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-stone-500 hover:text-saffron" data-testid="back-to-results">
                    <ArrowLeft className="h-4 w-4" /> Back to results
                </Link>
                <h1 className="mt-3 font-display font-black text-3xl sm:text-4xl text-navy">Traveller details</h1>
                {data.demo && (
                    <div className="mt-4 rounded-xl border border-gold/60 bg-gold/15 px-4 py-3 text-sm text-navy flex gap-2 items-start" data-testid="demo-checkout-banner">
                        <Sparkles className="h-4 w-4 mt-0.5 text-saffron shrink-0" />
                        <span><b>Demo fares.</b> This creates a sample booking request only — no money moves and no real ticket is issued.</span>
                    </div>
                )}

                <div className="mt-6 grid lg:grid-cols-[1fr_360px] gap-6 items-start">
                    <div className="space-y-5">
                        {passengers.map((p, i) => (
                            <div key={i} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`passenger-card-${i}`}>
                                <p className="font-bold text-navy flex items-center gap-2 mb-4">
                                    <User className="h-4 w-4 text-saffron" />
                                    {p.type === "adult" ? `Adult ${passengers.slice(0, i + 1).filter((x) => x.type === "adult").length}`
                                        : p.type === "child" ? `Child ${passengers.slice(0, i + 1).filter((x) => x.type === "child").length}`
                                        : `Infant ${passengers.slice(0, i + 1).filter((x) => x.type === "infant").length}`}
                                    <span className="text-[11px] font-semibold text-stone-400">as on govt-issued ID</span>
                                </p>
                                <div className="grid sm:grid-cols-3 gap-3">
                                    <input data-testid={`pax-${i}-first`} placeholder="First name" value={p.first_name}
                                        onChange={(e) => setPax(i, { first_name: e.target.value })}
                                        className="rounded-xl border border-stone-300 px-3.5 py-2.5 text-sm font-semibold text-navy focus:outline-none focus:ring-2 focus:ring-saffron/60" />
                                    <input data-testid={`pax-${i}-last`} placeholder="Last name" value={p.last_name}
                                        onChange={(e) => setPax(i, { last_name: e.target.value })}
                                        className="rounded-xl border border-stone-300 px-3.5 py-2.5 text-sm font-semibold text-navy focus:outline-none focus:ring-2 focus:ring-saffron/60" />
                                    <select data-testid={`pax-${i}-gender`} value={p.gender} onChange={(e) => setPax(i, { gender: e.target.value })}
                                        className="rounded-xl border border-stone-300 px-3.5 py-2.5 text-sm font-semibold text-navy">
                                        <option value="">Gender</option><option value="male">Male</option>
                                        <option value="female">Female</option><option value="other">Other</option>
                                    </select>
                                    {(p.type !== "adult" || international) && (
                                        <div>
                                            <label className="block text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">
                                                Date of birth{p.type === "adult" ? " (optional)" : ""}
                                            </label>
                                            <input type="date" data-testid={`pax-${i}-dob`} value={p.dob} max={new Date().toISOString().slice(0, 10)}
                                                onChange={(e) => setPax(i, { dob: e.target.value })}
                                                className="w-full rounded-xl border border-stone-300 px-3.5 py-2.5 text-sm font-semibold text-navy" />
                                        </div>
                                    )}
                                </div>
                                {international && (
                                    <div className="grid sm:grid-cols-2 gap-3 mt-3">
                                        <input data-testid={`pax-${i}-passport`} placeholder="Passport number (if issued)" value={p.passport_no}
                                            onChange={(e) => setPax(i, { passport_no: e.target.value })}
                                            className="rounded-xl border border-stone-300 px-3.5 py-2.5 text-sm font-semibold text-navy" />
                                        <input type="date" data-testid={`pax-${i}-passport-exp`} value={p.passport_expiry} title="Passport expiry"
                                            onChange={(e) => setPax(i, { passport_expiry: e.target.value })}
                                            className="rounded-xl border border-stone-300 px-3.5 py-2.5 text-sm font-semibold text-navy" />
                                    </div>
                                )}
                            </div>
                        ))}

                        <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="contact-card">
                            <p className="font-bold text-navy mb-1">Contact details</p>
                            <p className="text-xs text-stone-400 mb-4">Booking updates and your e-ticket go here.</p>
                            <div className="grid sm:grid-cols-3 gap-3">
                                <input data-testid="contact-name" placeholder="Full name" value={contact.name}
                                    onChange={(e) => setContact({ ...contact, name: e.target.value })}
                                    className="rounded-xl border border-stone-300 px-3.5 py-2.5 text-sm font-semibold text-navy" />
                                <input data-testid="contact-email" type="email" placeholder="Email" value={contact.email}
                                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                                    className="rounded-xl border border-stone-300 px-3.5 py-2.5 text-sm font-semibold text-navy" />
                                <input data-testid="contact-phone" placeholder="Mobile (10 digits)" value={contact.phone}
                                    onChange={(e) => setContact({ ...contact, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                                    className="rounded-xl border border-stone-300 px-3.5 py-2.5 text-sm font-semibold text-navy" />
                            </div>
                            <div className="mt-3">
                                <input data-testid="referral-code" placeholder="Referral code (optional)" value={refCode}
                                    onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                                    className="w-full sm:w-64 rounded-xl border border-stone-300 px-3.5 py-2.5 text-sm font-semibold text-navy" />
                            </div>
                        </div>

                        <label className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-white p-5 cursor-pointer" data-testid="consent-row">
                            <input type="checkbox" data-testid="consent-check" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
                            <span className="text-sm text-stone-600">
                                I've reviewed the fare rules, baggage and refund conditions, and I agree to GhumneChaley's{" "}
                                <Link to="/terms" className="text-saffron font-bold underline">terms</Link> and{" "}
                                <Link to="/privacy-policy" className="text-saffron font-bold underline">privacy notice</Link>.
                                I understand a booking is confirmed only after the airline issues the ticket.
                            </span>
                        </label>
                    </div>

                    {/* fare summary */}
                    <aside className="rounded-2xl border border-stone-200 bg-white p-5 lg:sticky lg:top-28" data-testid="fare-summary">
                        <p className="font-display font-black text-lg text-navy">Trip summary</p>
                        <div className="mt-3 space-y-3">
                            {offers.map((o, i) => (
                                <div key={i} className="rounded-xl bg-cream/60 border border-stone-100 p-3 text-sm">
                                    <p className="font-bold text-navy flex items-center gap-2"><Plane className="h-3.5 w-3.5 text-saffron rotate-90" />{o.segments[0].from} → {o.segments[o.segments.length - 1].to}</p>
                                    <p className="text-xs text-stone-500 mt-0.5">{o.airline} {o.flight_nos.join(" · ")}</p>
                                    <p className="text-xs text-stone-500">{fmtDate(o.segments[0].depart)} · {fmtTime(o.segments[0].depart)} · {fmtDur(o.duration_min)} · {o.stops ? `${o.stops} stop` : "Non-stop"}</p>
                                    <p className="text-[11px] text-stone-400 mt-1">{o.fare_family} · {o.baggage.checkin_kg}kg · {o.refundable ? "Refundable" : "Non-refundable"}</p>
                                </div>
                            ))}
                        </div>
                        {estimate && (
                            <div className="mt-4 text-sm space-y-1.5">
                                <div className="flex justify-between text-stone-600"><span>Base fare × travellers</span><span>{inr(estimate.base)}</span></div>
                                <div className="flex justify-between text-stone-600"><span>Taxes & fees</span><span>{inr(estimate.taxes)}</span></div>
                                <div className="flex justify-between pt-2 border-t border-stone-200 font-display font-black text-navy text-lg">
                                    <span>Total</span><span data-testid="checkout-total">{inr(estimate.total)}</span>
                                </div>
                                <p className="text-[11px] text-stone-400 flex items-center gap-1 pt-1"><ShieldCheck className="h-3 w-3 text-green-600" />Final amount is confirmed by our server before any payment. No hidden charges.</p>
                            </div>
                        )}
                        <button data-testid="create-booking-btn" onClick={submit} disabled={busy || !consent}
                            className="mt-4 w-full rounded-full bg-saffron text-white font-display font-black text-lg py-3.5 hover:bg-navy transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : meta?.booking_enabled ? "Continue to payment" : "Create booking request"}
                        </button>
                        <p className="mt-2 text-center text-[11px] text-stone-400">
                            {meta?.booking_enabled ? "Secured by Razorpay · UPI, cards, netbanking" : "Payment activates when live flight inventory is connected"}
                        </p>
                    </aside>
                </div>
            </main>
            <Footer />
        </div>
    );
}
